import { useCallback, useEffect, useState } from 'react';
import { ApiError, NetworkError, api } from '../api';
import { useI18n } from '../i18n';
import { Field, Modal, Spinner } from '../components';

/**
 * Capture from the sensor, on the Windows application (spec §7, HW-1).
 *
 * The window never touches the driver: it asks the application's local
 * endpoints, which run the TWAIN bridge beside the application. Each image
 * comes back as PNG with the pixel pitch the driver reported, is shown here,
 * and is saved through the ordinary upload — online to the server, offline
 * to the device — marked `source: 'sensor'`, or `simulator` with its
 * synthetic label when the simulated sensor was chosen.
 *
 * A second tab imports from the sensor software's own export folder, for a
 * clinic whose driver does not expose TWAIN or whose software they prefer.
 */
interface Source {
  id: string;
  name: string;
  kind: 'twain' | 'simulator';
  arch?: string;
  manufacturer?: string;
  version?: string;
}

interface Captured {
  fileName: string;
  mimeType: 'image/png' | 'image/jpeg';
  base64: string;
  byteSize: number;
  width: number | null;
  height: number | null;
  bitsPerPixel: number | null;
  dpiX: number | null;
  pixelSizeMm: number | null;
}

const KINDS = ['periapical', 'bitewing', 'panoramic', 'cephalometric', 'intraoral_photo', 'extraoral_photo', 'other'] as const;

export function SensorCapture({
  patientId,
  defaultToothFdi = null,
  defaultZone = null,
  onClose,
  onSaved,
}: {
  patientId: string;
  defaultToothFdi?: string | null;
  defaultZone?: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t, tx } = useI18n();
  const c = t.capture;
  const [tab, setTab] = useState<'sensor' | 'folder' | 'settings'>('sensor');
  const [sources, setSources] = useState<Source[] | null>(null);
  const [sourceId, setSourceId] = useState<string>('');
  const [showUi, setShowUi] = useState(true);
  const [kind, setKind] = useState<string>(defaultZone ? 'extraoral_photo' : 'periapical');
  const [toothFdi, setToothFdi] = useState(defaultToothFdi ?? '');
  const [capturing, setCapturing] = useState(false);
  const [images, setImages] = useState<(Captured & { source: Source })[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [folder, setFolder] = useState<{ folder: string | null; exists: boolean; files: { path: string; name: string; size: number; modifiedAt: string; isNew: boolean }[] } | null>(null);
  const [settings, setSettings] = useState<{ preferredSourceId: string | null; watchFolder: string | null; showUi: boolean; transfer: 'memory' | 'native' } | null>(null);
  const [selfTest, setSelfTest] = useState<string | null>(null);

  const loadSources = useCallback(async () => {
    try {
      const r = (await (await fetch('/api/local/capture/sources')).json()) as { sources: Source[] };
      setSources(r.sources);
      const s = (await (await fetch('/api/local/capture/settings')).json()) as { preferredSourceId: string | null; watchFolder: string | null; showUi: boolean; transfer: 'memory' | 'native' };
      setSettings(s);
      setShowUi(s.showUi);
      const preferred = s.preferredSourceId && r.sources.some((x) => x.id === s.preferredSourceId) ? s.preferredSourceId : (r.sources.find((x) => x.kind === 'twain')?.id ?? 'simulator');
      setSourceId(preferred);
    } catch {
      setSources([]);
    }
  }, []);

  const loadFolder = useCallback(async () => {
    try {
      setFolder((await (await fetch('/api/local/capture/folder')).json()) as typeof folder);
    } catch {
      setFolder(null);
    }
  }, []);

  useEffect(() => {
    void loadSources();
    void loadFolder();
  }, [loadSources, loadFolder]);

  const source = sources?.find((s) => s.id === sourceId) ?? null;

  async function capture() {
    if (!source) return;
    setCapturing(true);
    setError(null);
    try {
      const r = (await (
        await fetch('/api/local/capture/acquire', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ sourceId: source.id, showUi, timeoutSeconds: 180 }) })
      ).json()) as { ok: boolean; images: Captured[]; error: string | null };
      if (!r.ok || r.images.length === 0) {
        setError((c.errors as Record<string, string>)[r.error ?? ''] ?? c.failed);
        return;
      }
      setImages((list) => [...list, ...r.images.map((img) => ({ ...img, source }))]);
      await fetch('/api/local/capture/settings', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ preferredSourceId: source.id, showUi }) });
    } catch {
      setError(c.failed);
    } finally {
      setCapturing(false);
    }
  }

  async function importFile(path: string) {
    setError(null);
    try {
      const r = (await (await fetch('/api/local/capture/folder/file', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ path }) })).json()) as { ok: boolean; image: Captured | null; error: string | null };
      if (!r.ok || !r.image) {
        setError(c.failed);
        return;
      }
      setImages((list) => [...list, { ...r.image!, source: { id: 'folder', name: folder?.folder ?? '', kind: 'twain' } }]);
      setTab('sensor');
      void loadFolder();
    } catch {
      setError(c.failed);
    }
  }

  async function saveAll() {
    setSaving(true);
    setError(null);
    let n = 0;
    try {
      for (const img of images) {
        const simulated = img.source.kind === 'simulator';
        const fromFolder = img.source.id === 'folder';
        await api.uploadImage({
          patientId,
          kind,
          mimeType: img.mimeType,
          data: img.base64,
          toothFdi: defaultZone || !toothFdi.trim() || ['panoramic', 'cephalometric', 'extraoral_photo'].includes(kind) ? null : toothFdi.trim(),
          zone: defaultZone ?? undefined,
          surfaces: [],
          acquiredAt: new Date().toISOString(),
          source: simulated ? 'simulator' : fromFolder ? 'import' : 'sensor',
          device: simulated ? { kind: 'simulator', model: img.source.name } : fromFolder ? { kind: 'folder', model: img.source.name } : { kind: 'twain', model: img.source.name, driver: img.source.version ?? null },
          ...(img.pixelSizeMm ? { annotations: { version: 1, calibration: { pxPerMm: 1 / img.pixelSizeMm, method: 'pixel_size', note: `${Math.round(img.pixelSizeMm * 1000)} µm from the driver` }, measurements: [] } } : {}),
        });
        n += 1;
        setSavedCount(n);
      }
      onSaved();
    } catch (e) {
      setError(e instanceof NetworkError ? t.errors.network : e instanceof ApiError ? e.message : t.common.error);
    } finally {
      setSaving(false);
    }
  }

  async function saveSettings(patch: Record<string, unknown>) {
    const s = (await (await fetch('/api/local/capture/settings', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(patch) })).json()) as typeof settings;
    setSettings(s);
    void loadFolder();
  }

  async function runSelfTest() {
    setSelfTest('…');
    try {
      const r = (await (await fetch('/api/local/capture/selftest', { method: 'POST' })).json()) as { bridges: { arch: string; present: boolean; dsm: string | null; sources: number; selftest: string | null; error: string | null }[] };
      setSelfTest(r.bridges.map((b) => tx(c.selfTestResult, { arch: b.arch, result: b.present ? `${b.selftest ?? '—'} · DSM ${b.dsm ?? '—'} · ${b.sources} source(s)` : 'missing' })).join('\n'));
    } catch {
      setSelfTest(c.failed);
    }
  }

  const wholeMouth = ['panoramic', 'cephalometric', 'extraoral_photo'].includes(kind);

  return (
    <Modal
      title={c.title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving || capturing}>
            {t.patients.cancel}
          </button>
          <button type="button" className="btn" onClick={() => void saveAll()} disabled={saving || capturing || images.length === 0}>
            {saving ? (
              <>
                <Spinner /> {c.saving} {savedCount}/{images.length}
              </>
            ) : (
              c.saveAll
            )}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="page-sub" style={{ margin: 0 }}>{c.sub}</p>
        <div className="segmented" role="tablist">
          {(['sensor', 'folder', 'settings'] as const).map((k) => (
            <button key={k} type="button" aria-pressed={tab === k} onClick={() => setTab(k)}>
              {k === 'sensor' ? c.source : k === 'folder' ? c.folderTitle : c.settings}
            </button>
          ))}
        </div>
        {error ? (
          <div className="notice danger" role="alert">
            <span>{error}</span>
          </div>
        ) : null}

        {tab === 'sensor' ? (
          <>
            <div className="grid-2">
              <Field label={c.source} required>
                <select value={sourceId} onChange={(e) => setSourceId(e.target.value)} disabled={capturing}>
                  {(sources ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                      {s.manufacturer ? ` — ${s.manufacturer}` : ''}
                      {s.arch ? ` (${s.arch})` : ''}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={c.kind} required>
                <select value={kind} onChange={(e) => setKind(e.target.value)} disabled={capturing}>
                  {KINDS.map((k) => (
                    <option key={k} value={k}>
                      {(t.imaging.kinds as Record<string, string>)[k] ?? k}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            {sources && !sources.some((s) => s.kind === 'twain') ? (
              <div className="notice">
                <span>{c.noSources}</span>
              </div>
            ) : null}
            {source?.kind === 'simulator' ? (
              <div className="notice warn">
                <span>{c.simulatorNote}</span>
              </div>
            ) : null}
            <div className="grid-2">
              {!defaultZone && !wholeMouth ? (
                <Field label={c.tooth} hint={t.patients.optional}>
                  <input value={toothFdi} onChange={(e) => setToothFdi(e.target.value.replace(/\D/g, '').slice(0, 2))} inputMode="numeric" className="ltr-value" placeholder="36" disabled={capturing} />
                </Field>
              ) : (
                <div />
              )}
              {source?.kind === 'twain' ? (
                <label className="row" style={{ gap: 8, alignItems: 'center', alignSelf: 'end', paddingBottom: 10 }}>
                  <input type="checkbox" checked={showUi} onChange={(e) => setShowUi(e.target.checked)} disabled={capturing} />
                  <span>{c.showUi}</span>
                </label>
              ) : null}
            </div>
            <div>
              <button type="button" className="btn" onClick={() => void capture()} disabled={capturing || !source}>
                {capturing ? (
                  <>
                    <Spinner /> {c.capturing}
                  </>
                ) : (
                  c.capture
                )}
              </button>
            </div>
            {images.length > 0 ? (
              <div className="stack">
                <div className="section-label">{tx(c.captured, { n: images.length })}</div>
                <div className="thumb-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
                  {images.map((img, i) => (
                    <div key={`${img.fileName}-${i}`} className="thumb" style={{ position: 'relative' }}>
                      <img src={`data:${img.mimeType};base64,${img.base64}`} alt={c.preview} />
                      {img.source.kind === 'simulator' ? <span className="thumb-tag warn">{t.chart.simulated}</span> : null}
                      <span className="thumb-meta">
                        {img.width && img.height ? <span className="ltr-value">{img.width}×{img.height}</span> : null}
                        {img.bitsPerPixel ? <span className="ltr-value"> · {img.bitsPerPixel} bit</span> : null}
                      </span>
                      <button type="button" className="btn btn-ghost btn-sm" style={{ position: 'absolute', top: 6, insetInlineEnd: 6 }} onClick={() => setImages((list) => list.filter((_, j) => j !== i))} disabled={saving}>
                        {c.discard}
                      </button>
                    </div>
                  ))}
                </div>
                <p className="hint" style={{ color: 'var(--ink-3)', fontSize: 12, margin: 0 }}>
                  {images[0]?.pixelSizeMm ? tx(c.pixelSize, { um: Math.round(images[0].pixelSizeMm * 1000) }) : c.noPixelSize}
                </p>
              </div>
            ) : null}
          </>
        ) : null}

        {tab === 'folder' ? (
          <>
            <p className="page-sub" style={{ margin: 0 }}>{c.folderSub}</p>
            <Field label={c.folderPath}>
              <input className="ltr-value" defaultValue={settings?.watchFolder ?? ''} placeholder="C:\\Sensor\\Export" onBlur={(e) => void saveSettings({ watchFolder: e.target.value })} />
            </Field>
            {!folder?.folder ? (
              <div className="notice">
                <span>{c.folderNone}</span>
              </div>
            ) : !folder.exists ? (
              <div className="notice danger">
                <span>{c.folderMissing}</span>
              </div>
            ) : folder.files.length === 0 ? (
              <div className="notice">
                <span>{c.folderEmpty}</span>
              </div>
            ) : (
              <table className="table">
                <tbody>
                  {folder.files.map((f) => (
                    <tr key={f.path}>
                      <td>
                        <span className="ltr-value">{f.name}</span> {f.isNew ? <span className="badge brand">{c.folderNew}</span> : null}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', color: 'var(--ink-3)' }}>{new Date(f.modifiedAt).toLocaleString()}</td>
                      <td style={{ textAlign: 'end' }}>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => void importFile(f.path)}>
                          {c.import}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => void loadFolder()}>
                {c.refresh}
              </button>
            </div>
          </>
        ) : null}

        {tab === 'settings' ? (
          <>
            <Field label={c.transfer}>
              <select value={settings?.transfer ?? 'memory'} onChange={(e) => void saveSettings({ transfer: e.target.value })}>
                <option value="memory">{c.transferMemory}</option>
                <option value="native">{c.transferNative}</option>
              </select>
            </Field>
            <div>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => void runSelfTest()}>
                {c.selfTest}
              </button>
            </div>
            {selfTest ? <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, margin: 0 }}>{selfTest}</pre> : null}
          </>
        ) : null}
      </div>
    </Modal>
  );
}
